"use client";

import React from "react";
import Link from "next/link";
import RelatedTopics from "@/components/tools/RelatedTopics";
import Hero from "@/components/tools/Hero";
import WhatCausesDiscoloration from "@/components/services/teeth-whitening/WhatCausesDiscoloration";
import TypesOfWhitening from "@/components/services/teeth-whitening/TypesOfWhitening";
import WhiteningCost from "@/components/services/teeth-whitening/WhiteningCost";
import WhyProfessionalSafer from "@/components/services/teeth-whitening/WhyProfessionalSafer";
import ChooseSmile4Me from "@/components/services/teeth-whitening/ChooseSmile4Me";
import ReadyForBrighterSmile from "@/components/services/teeth-whitening/ReadyForBrighterSmile";
import FAQ from "@/components/tools/FAQ";

const teethWhiteningFAQs = [
  {
    question: "What's the best way to whiten my smile?",
    answer:
      "As a trusted dentist in Astoria, we offer professional teeth whitening, the best option because we use more potent, safer whitening agents than over-the-counter products, delivering faster, longer-lasting results.",
  },
  {
    question: "Is professional whitening safe for sensitive teeth?",
    answer:
      "Yes, with the right approach. We customize the concentration and can apply desensitizing treatments before or after the procedure. Patients with sensitivity often do well with a gradual take-home protocol.",
  },
  {
    question: "Will whitening work on my crowns or veneers?",
    answer:
      "Whitening agents don't affect dental restorations. We'll assess your restorations at your consultation and discuss whether color-matched replacements make sense.",
  },
  {
    question: "How many shades whiter can I expect?",
    answer:
      "In-office treatments typically deliver 4–8 shades of improvement in a single session. Results vary based on baseline shade and stain type. Your shade assessment sets realistic expectations before we begin.",
  },
  {
    question: "How soon can I get a teeth-whitening appointment?",
    answer:
      "Smile4Me Dental Care offers same-day appointments for teeth whitening in Astoria. You can brighten your smile without waiting weeks for treatment. Simply call our office or book online, and we'll schedule your appointment at a time that works best for you.",
  },
];

const TeethWhitening: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/photo-close-up-smile-split-before-after-yellow-stained-teeth-and-white-bright-teeth-teeth-whitening-astoria-results-female-lips-and-teeth-comparison.jpg"
        imageAlt="Professional Teeth Whitening Astoria NY at Smile4Me Dental Care"
        title="Professional Teeth Whitening Astoria NY Offers"
        subtitle="Brighten your smile with safe, professional whitening treatments supervised by Dr. Khaimov."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
      <main className="flex-1 py-8">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <div className="w-full py-6 sm:py-8 bg-gradient-to-b from-white to-gray-50">
              <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 space-y-6 text-center md:text-left">
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
                  A brighter smile doesn&apos;t just enhance your appearance; it
                  can boost your confidence and improve your overall quality of
                  life.{" "}
                  <Link
                    href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6290526/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
                    aria-label="Research on teeth whitening benefits"
                  >
                    Research
                  </Link>{" "}
                  shows that patients who undergo teeth whitening experience
                  significant improvements in their oral health perception,
                  self-esteem, and social interactions.
                </p>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
                  Professional teeth whitening in Astoria is available at{" "}
                  <Link
                    href="/contact/"
                    className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
                    aria-label="Contact Smile4Me Dental Care"
                  >
                    Smile4Me Dental Care
                  </Link>
                  , where Dr. Khaimov supervises every treatment. Results are
                  noticeable in a single appointment. That&apos;s far safer and
                  more effective than any drugstore kit. A brighter smile builds
                  confidence &mdash; and it&apos;s worth doing right.
                </p>
              </div>
            </div>
            <WhatCausesDiscoloration />
            <TypesOfWhitening />
            <WhiteningCost />
            <WhyProfessionalSafer />
            <ChooseSmile4Me />
            <ReadyForBrighterSmile />
            <FAQ
              title="Frequently Asked Questions"
              faqs={teethWhiteningFAQs}
            />
          </article>
          <RelatedTopics
            topics={[
              {
                label: "Cosmetic Dentistry",
                url: "/services/cosmetic-dentistry",
              },
              {
                label: "Opalescence Whitening",
                url: "/services/opalescence",
              },
              {
                label: "Veneers",
                url: "/services/veneers",
              },
              {
                label: "Ceramic Crowns",
                url: "/services/ceramic-crowns",
              },
              {
                label: "Dental Cleanings",
                url: "/services/dental-cleanings",
              },
              {
                label: "Dental Bonding",
                url: "/services/dental-bonding",
              },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default TeethWhitening;
